import * as React from "react";
import {
  Historical,
  Metadata,
  Query,
  SimpleTableRow,
  SortDirection,
  TableHeader,
} from "../../interfaces";
import { Button, Divider, Grid, Theme, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { AppBody } from "../AppBody";
import LinkComponent from "../LinkComponent";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import {
  appHeaderTitle,
  fileNameFormat,
  getExpandState,
} from "../../common/services/Utilities";
import useSortProperties from "../../hooks/useSortProperties";
import { buildQueryUrl, fetchData } from "../../common/services/ApiClient";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import ResultPagination from "../../containers/ResultPagination";
import {
  CBFinancialFilterOptions,
  CBStructureFilterOptions,
  SIFinancialFilterOptions,
  SIStructureFilterOptions,
} from "./FieldSelectionOptions";
import { ApiBaseUrl, AppUrl } from "../../config";
import { useHistory } from "react-router-dom";
import HistoricalSearchForm from "../../containers/HistoricalSearchForm";
import ApiDataDownload from "../ApiDataDownload";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

type HistoricalApiField = keyof Historical;

interface Props {
  appState: Query;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  updateEndDate: (year: string) => void;
}

interface HistoricalHeaders {
  title: string;
  apiField: HistoricalApiField;
}

const useStyles = makeStyles((theme: Theme) => ({
  tableStyles: {
    "& #multi-header-table": {
      "marginTop": 0,
      "& #multi-header-table-head": {
        backgroundColor: "#003256",
      },
    },
    "& #tableWrapper": {
      height: 460,
      marginTop: 8,
    },
    "& th": {
      zIndex: 5,
      position: "sticky",
      top: 0,
    },
    "& tr > th#year-header-0": {
      "position": "sticky",
      "left": 0,
      "zIndex": 20,
      "backgroundColor": "#003256",
      "& span": {
        width: 49,
      },
    },
    "& tr > th#locations-header-1": {
      position: "sticky",
      left: 65,
      zIndex: 20,
      backgroundColor: "#003256",
    },
    "& tr:nth-child(odd) > td:nth-child(1)": {
      position: "sticky",
      left: 0,
      zIndex: 10,
      backgroundColor: "#D2E1EE",
    },
    "& tr:nth-child(even) > td:nth-child(1)": {
      position: "sticky",
      left: 0,
      zIndex: 10,
      backgroundColor: "#D9E8F6",
    },
    "& tr:nth-child(odd) > td:nth-child(2)": {
      position: "sticky",
      left: 65,
      zIndex: 10,
      backgroundColor: "#D2E1EE",
    },
    "& tr:nth-child(even) > td:nth-child(2)": {
      position: "sticky",
      left: 65,
      zIndex: 10,
      backgroundColor: "#D9E8F6",
    },
  },
  buttonStyle: {
    "color": theme.palette.primary.main,
    "border": "0px solid",
    "borderRadius": "3px",
    "padding": 0,
    "height": "auto",
    "textDecoration": "underline",
    "&:focus, &:hover": {
      color: "#BB3300",
      backgroundColor: "#E6F3FF",
      textDecoration: "underline",
    },
    "&:active": {
      color: "#fff",
      backgroundColor: theme.palette.primary.main,
      textDecoration: "none",
    },
  },
}));

const staticHeaders: HistoricalHeaders[] = [
  { title: "Year", apiField: "YEAR" },
  { title: "Locations", apiField: "STNAME" },
];

const CBSHeaders = [].concat.apply(
  [],
  CBStructureFilterOptions.map(report =>
    report.fields.map(field => {
      return { title: field.label, apiField: field.value };
    }),
  ) as never[],
);
const CBFHeaders = [].concat.apply(
  [],
  CBFinancialFilterOptions.map(report =>
    report.fields.map(field => {
      return {
        title: `${field.label}${field.thousandsUS ? "*" : ""}`,
        apiField: field.value,
        thousandsUS: field.thousandsUS,
      };
    }),
  ) as never[],
);
const SISHeaders = [].concat.apply(
  [],
  SIStructureFilterOptions.map(report =>
    report.fields.map(field => {
      return {
        title: `${field.label}${field.specialNote ? "**" : ""}`,
        apiField: field.value,
        specialNote: field.specialNote,
      };
    }),
  ) as never[],
);
const SIFHeaders = [].concat.apply(
  [],
  SIFinancialFilterOptions.map(report =>
    report.fields.map(field => {
      return {
        title: `${field.label}${field.thousandsUS ? "*" : ""}`,
        apiField: field.value,
        thousandsUS: field.thousandsUS,
      };
    }),
  ) as never[],
);

export function historicalHeaderLookup(report: string) {
  const headers: any = {
    CBS: CBSHeaders,
    CBF: CBFHeaders,
    SIS: SISHeaders,
    SIF: SIFHeaders,
  };
  return headers[report];
}

export function transformHistoricalData(
  historicalData: Historical[],
  headerData: HistoricalHeaders[],
) {
  return historicalData.map((data: Historical) => {
    const tableCellData: SimpleTableRow = {};
    headerData
      .map((header: HistoricalHeaders) => header.apiField)
      .forEach((apiField: HistoricalApiField) => {
        tableCellData[apiField] = {
          value: data[apiField]
            ? data[apiField].toLocaleString()
            : data[apiField],
        };
      });
    return tableCellData;
  });
}

export default function HistoricalBankData({
  appState,
  updateSort,
  updateEndDate,
}: Props) {
  const classes = useStyles();
  //used to call the useEffect when location needs to reset to previous

  const history = useHistory();

  const [tableNote, setTableNote] = React.useState("");
  const [historicalData, setHistoricalData] = React.useState<Historical[]>([]);
  const [metaData, setMetaData] = React.useState<Metadata>({
    count: 0,
  });
  const [endYear, setEndYear] = React.useState(0);

  //combines static headers and headers based on lookup.
  const headerValues = [
    ...staticHeaders,
    ...historicalHeaderLookup(appState.filters.selectedReport),
  ].filter(
    (value, index, array) =>
      array.findIndex(arrayVal => arrayVal.apiField === value.apiField) ===
      index,
  );

  const combinedDisplayFields =
    appState.displayFields && appState.fixedDisplayFields
      ? appState.displayFields.concat(appState.fixedDisplayFields)
      : [];

  const HistoricalHeadersMetaData = headerValues.filter(header =>
    combinedDisplayFields.includes(header.apiField),
  );

  React.useEffect(() => {
    setTableNote("");
    return HistoricalHeadersMetaData.forEach(header => {
      if (header.thousandsUS) {
        return setTableNote("* Numbers represent thousands of US Dollars");
      }
      if (header.specialNote) {
        return setTableNote(
          "** State level institution count is currently unavailable",
        );
      }
    });
  }, [HistoricalHeadersMetaData]);

  const HistoricalHeaders: TableHeader[][] = [
    HistoricalHeadersMetaData.map((header: HistoricalHeaders) => ({
      title: header.title,
      styles: {},
      colSpan: 1,
      apiField: header.apiField,
    })),
  ];

  const helpPathSiCB = appState.filters.selectedReport.slice(0, -1);
  const introParagraph = (
    <>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          {appHeaderTitle("historical")}
        </Grid>
        <Grid item xs={1}>
          <Button
            color="primary"
            disableElevation
            className={classes.buttonStyle}
            aria-label={`View help for ${helpPathSiCB}.`}
            id={`view-help-link-${helpPathSiCB}`}
            onClick={() =>
              history.push({
                pathname: `${AppUrl("HSOB")}/help/${helpPathSiCB}`,
                state: {
                  url: AppUrl("HSOB") + window.location.search,
                },
              })
            }
          >
            Help
          </Button>
        </Grid>
      </Grid>

      <Typography variant="body2" id="historical-description">
        BankFind Suite's aggregate historical bank data is the annual summary of
        financial and structure data for all FDIC-insured institutions
        headquartered in a state, city or US territory. The data can be used to
        identify and analyze long term trends and to develop benchmarks to
        evaluate the current conditions of the banking and thrift industries.
        This annual data is available as of December 31 for the years 1934 to{" "}
        {endYear}
      </Typography>
      <LinkComponent appName={"HistoricalBankData"} />
    </>
  );

  //Sets end year based on API
  React.useEffect(() => {
    if (endYear === 0) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/summary?limit=1&offset=0&sort_by=YEAR&sort_order=DESC`,
        )
          .then(response => {
            if (isMounted) {
              setEndYear(response.data[0].YEAR);
              updateEndDate(response.data[0].YEAR.toString());
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [endYear, updateEndDate]);

  React.useEffect(() => {
    if (appState.filters.endDate) {
      const updatedAppState = {
        ...appState,
        filters: {
          ...appState.filters,
          selectedReport: appState.filters.selectedReport.slice(0, -1),
          selectedStates:
            appState.filters.selectedStates.length > 0
              ? appState.filters.selectedStates
              : [undefined],
        },
      };
      let isMounted = true;
      (async () => {
        await fetchData(buildQueryUrl("/summary", updatedAppState))
          .then(response => {
            if (isMounted) {
              setHistoricalData(response.data);
              setMetaData(response.totals);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [appState]);

  useInitialPageLoad("historical");
  return (
    <>
      <Breadcrumbs currentPageBreadCrumbTitle={"Historical Bank Data"} />
      <CloudFoundryBanner />
      <AppBody
        introText={introParagraph}
        searchForm={<HistoricalSearchForm endYear={endYear} />}
        expand={getExpandState(appState.ui.searchPanelExpand, true)}
      >
        <>
          <Typography component="div" id="multi-header-table-title">
            <Typography
              variant="h2"
              id="resultSummary"
              style={{
                position: "relative",
              }}
            >
              {metaData.count.toLocaleString()}{" "}
              {metaData.count === 1 ? "Matching Result" : "Matching Results"}
              <ApiDataDownload
                app="historical"
                apiData={[
                  {
                    appState: {
                      ...appState,
                      filters: {
                        ...appState.filters,
                        selectedReport: appState.filters.selectedReport.slice(
                          0,
                          -1,
                        ),
                      },
                    },
                    fileName: `${fileNameFormat("Summary of Results")}.csv`,
                    apiPath: "summary",
                  },
                ]}
              />
            </Typography>
            <Divider style={{ marginBottom: "1rem" }} />
          </Typography>
          <ResultPagination count={metaData.count} hideBottomPagination>
            <div className={classes.tableStyles}>
              {tableNote !== "" ? (
                <Typography
                  variant="h4"
                  id="tableNote"
                  style={{ paddingBottom: 0 }}
                >
                  {tableNote}
                </Typography>
              ) : (
                ""
              )}
              <MultiHeaderTable
                sortProperties={useSortProperties(appState, updateSort)}
                tableHeaders={HistoricalHeaders}
                tableBody={
                  <SimpleTableBody
                    tableData={transformHistoricalData(
                      historicalData,
                      HistoricalHeadersMetaData,
                    )}
                  />
                }
              />
            </div>
          </ResultPagination>
        </>
      </AppBody>
    </>
  );
}
